import React, { useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import Breakpoints from 'components/Breakpoints';
import UserInfo from 'components/UserInfo';
import isMock from 'utilities/isMock';
import isNonProduction from 'utilities/isNonProduction';
import { useTheme } from '@mui/material/styles';

interface DevelopmentInfoProps {
  themeMode?: 'light' | 'dark';
  onThemeModeChange?: (mode: 'light' | 'dark') => void;
  tenantId?: number;
  tenantIdOverride?: number;
  onTenantOverrideChange?: (id: number) => void;
}

// Dev-only tenant theme override options (0 = use the invoice's real tenant).
const TENANT_OPTIONS = [
  { id: 0, label: 'No override (use invoice)' },
  { id: 3, label: 'Wayroo (3)' },
  { id: 4, label: 'Paparazzi (4)' },
  { id: 5, label: 'Jordan Essentials (5)' },
];

function DevelopmentInfo({
  themeMode = 'light',
  onThemeModeChange,
  tenantId,
  tenantIdOverride = 0,
  onTenantOverrideChange,
}: DevelopmentInfoProps): JSX.Element {
  const theme = useTheme();
  const [isVisible, setIsVisible] = useState(true);
  const [breakpointIsVisible, setBreakpointIsVisible] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  if (!isVisible || !isNonProduction) {
    return <span />;
  }

  return (
    <Box
      style={{
        position: 'fixed',
        bottom: '1rem',
        left: '1rem',
        zIndex: '30',
        textAlign: 'end',
      }}
    >
      <Chip
        label="Developer Info"
        onDelete={() => setIsVisible(false)}
        onClick={() => setIsExpanded(!isExpanded)}
        color="secondary"
        sx={{
          fontWeight: 'bold',
          backgroundColor: theme.palette.secondary.main,
        }}
        variant="filled"
      />{' '}
      {breakpointIsVisible && (
        <Chip
          label={<Breakpoints />}
          color="success"
          variant="filled"
          onDelete={() => setBreakpointIsVisible(false)}
          sx={{ backgroundColor: 'rgba(255,255,255,.75)' }}
        ></Chip>
      )}
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box mt={1}>
          <TableContainer
            component={Paper}
            sx={{ maxHeight: '90vh', maxWidth: '100vw' }}
          >
            <Table aria-label="environment variables" size="small">
              <TableHead>
                <TableRow>
                  <TableCell colSpan={2}>
                    <Typography variant="h4">
                      Environment Information{' '}
                      <Chip
                        color="success"
                        sx={{ fontWeight: 'bold' }}
                        label="Only In Development"
                      />{' '}
                      {onThemeModeChange && (
                        <Chip
                          onClick={() =>
                            onThemeModeChange(
                              themeMode === 'light' ? 'dark' : 'light'
                            )
                          }
                          label={
                            themeMode === 'light'
                              ? 'To Dark Mode'
                              : 'To Light Mode'
                          }
                          variant="outlined"
                          clickable
                        />
                      )}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <strong>Variable</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Value</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Node Environment</TableCell>
                  <TableCell>{process.env.NODE_ENV}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    REACT_APP_SHOPPING_API_BASE_URL
                    <br />
                    <small>The API we are querying</small>
                  </TableCell>
                  <TableCell>
                    {process.env.REACT_APP_SHOPPING_API_BASE_URL}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    REACT_APP_SENTRY_DSN
                    <br />
                    <small>The sentry identifier</small>
                  </TableCell>
                  <TableCell>{process.env.REACT_APP_SENTRY_DSN}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    REACT_APP_SENTRY_ENVIRONMENT_NAME
                    <br />
                    <small>
                      Which environment error reporting is being sent to
                    </small>
                  </TableCell>
                  <TableCell>
                    {process.env.REACT_APP_SENTRY_ENVIRONMENT_NAME}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    REACT_APP_SENTRY_TRACE_RATE
                    <br />
                    <small>
                      The sample rate for reporting errors to Sentry
                    </small>
                  </TableCell>
                  <TableCell>
                    {process.env.REACT_APP_SENTRY_TRACE_RATE}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    REACT_APP_PAYPAL_CLIENT_ID
                    <br />
                    <small>PayPal client ID for checkout</small>
                  </TableCell>
                  <TableCell>
                    {process.env.REACT_APP_PAYPAL_CLIENT_ID}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    REACT_APP_PAYPAL_BN_CODE
                    <br />
                    <small>PayPal build notation code</small>
                  </TableCell>
                  <TableCell>{process.env.REACT_APP_PAYPAL_BN_CODE}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    REACT_APP_GIT_SHA
                    <br />
                    <small>Current deployed SHA</small>
                  </TableCell>
                  <TableCell>{process.env.REACT_APP_GIT_SHA}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    PORT
                    <br />
                    <small>Development server port</small>
                  </TableCell>
                  <TableCell>{process.env.PORT}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    PUBLIC_URL
                    <br />
                    <small>Base URL for the application</small>
                  </TableCell>
                  <TableCell>{process.env.PUBLIC_URL}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={2}>
                    <Typography variant="h5">
                      Application Information
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    Using Mock Data?
                    <br />
                    <small>Data is fake and generated from API contract</small>
                  </TableCell>
                  <TableCell>{isMock ? 'true' : 'false'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Tenant ID</TableCell>
                  <TableCell>{tenantId}</TableCell>
                </TableRow>
                {onTenantOverrideChange && (
                  <TableRow>
                    <TableCell>
                      Tenant Theme Override
                      <br />
                      <small>Preview a brand theme regardless of invoice</small>
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={tenantIdOverride}
                        onChange={(e) =>
                          onTenantOverrideChange(Number(e.target.value))
                        }
                      >
                        {TENANT_OPTIONS.map((t) => (
                          <MenuItem key={t.id} value={t.id}>
                            {t.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell>Store ID</TableCell>
                  {/* <TableCell>{storeId}</TableCell> */}
                </TableRow>
                <TableRow>
                  <TableCell colSpan={2}>
                    <Typography variant="h5" sx={{ paddingBotton: '1rem' }}>
                      User Information
                    </Typography>
                    <UserInfo />
                  </TableCell>
                </TableRow>
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={2}>
                    <Typography variant="caption">
                      This information is only available in development mode.
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Box>
      </Collapse>
    </Box>
  );
}

export default DevelopmentInfo;
